export const enResources = {
  translation: {
    brand: "Lumvio",
    language: { en: "English", ar: "العربية", switch: "Language" },
    common: {
      signIn: "Sign in",
      signUp: "Sign up",
      signOut: "Sign out",
      getStarted: "Get started",
      backToDashboard: "Back to dashboard",
      goToDashboard: "Go to dashboard",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving…",
      loading: "Loading…",
      retry: "Try again",
      continue: "Continue",
      upgrade: "Upgrade",
      email: "Email",
      password: "Password",
      fullName: "Full name",
      notSet: "Not set",
      notNow: "Not now",
      creating: "Creating…",
      preparing: "Preparing…",
      edit: "Edit",
      remove: "Remove",
      later: "Later",
      days: "days",
      uploading: "Uploading…",
      posting: "Posting…",
      post: "Post",
      user: "User",
    },
    nav: {
      features: "Features",
      howItWorks: "How it works",
      pricing: "Pricing",
      dashboard: "Dashboard",
      projects: "Projects",
      chat: "Chat",
      calendar: "Calendar",
      invoices: "Invoices",
      escrow: "Payment Protection",
      wallet: "Wallet",
      portfolio: "Portfolio",
      myPortfolio: "My portfolio",
      verify: "Verify identity",
      settings: "Settings",
      admin: "Admin",
      workspace: "Workspace",
      account: "Account",
      devices: "Devices",
      language: "Language",
      signOut: "Sign out",
      signedOut: "Signed out",
      verified: "Verified",
      accountDefault: "Account",
      roleFreelancer: "Freelancer",
      roleClient: "Client",
      roleAdmin: "Admin",
      roleUser: "User",
    },

    calendarPage: {
      badge: "Calendar",
      title: "Project calendar",
      subtitle: "Every deadline, kickoff, and delivery — in one calm month view.",
      today: "Today",
      prev: "Previous month",
      next: "Next month",
      selectedDay: "Selected day",
      upcoming: "Next 30 days",
      stats: { events: "Events", deadlines: "Deadlines", completed: "Delivered" },
      legend: { start: "Kickoff", deadline: "Deadline", completed: "Delivered" },
      kind: { start: "Kickoff", deadline: "Deadline", completed: "Delivered" },
      empty: {
        day: "Nothing scheduled on this day.",
        upcoming: "You're clear for the next 30 days.",
      },
    },

    home: {
      badge: "Verified freelancers only",
      heroTitle: "Run every client project in one calm workspace",
      heroTitlePrefix: "The client portal your",
      heroTitleEmphasis: "freelance business deserves.",
      heroSubtitle:
        "Ship projects, collect protected payments, and get paid globally — all behind an identity-verified gate designed to keep fake profiles off the platform.",
      heroSubtitlePrefix: "Ship projects, collect protected payments, and get paid",
      heroSubtitleEmphasis: "globally",
      heroSubtitleSuffix: "— all behind an identity-verified gate designed to keep fake profiles off the platform.",
      ctaStart: "Start free",
      ctaVerify: "Start free — verify to unlock",
      ctaSecurity: "See the Security Gate",
      ctaSignIn: "Sign in",
      trustedBy: "Trusted by independent freelancers worldwide",
      trustedByWorldClass: "Technology powering Lumvio",
      trustItems: ["Stripe card payments", "Payment protection", "Verified identity gate"],
      trust: {
        eyebrow: "Trust by design",
        title: "Built for freelancers who demand trust.",
        subtitle:
          "Three controls designed to protect both sides — identity checks, protected payments, and clear fees.",
        verifiedTitle: "Verified Identity",
        verifiedBody:
          "Every freelancer is identity-verified through Stripe Identity before working, which helps keep fake profiles off Lumvio.",
        escrowTitle: "Payment Protection",
        escrowBody:
          "Client funds are held under payment protection and released only when the delivered work is approved.",
        transparentTitle: "Transparent Payments",
        transparentBody:
          "Lumvio takes no commission from your project earnings. Third-party payment-processing fees still apply.",
      },

      power: {
        eyebrow: "Delivery, solved",
        title: "Send the whole project. Not a link.",
        subtitle:
          "40 GB per file, resumable to the last byte, and previewed in the browser — every delivery lives inside the workspace your client already trusts.",
        storageTitle: "Large file transfer",
        storageBody:
          "Drop raw footage, layered design files and full project folders straight into the project. No WeTransfer, no expiring links, no re-uploads when the Wi-Fi drops.",
        storagePoint1: "40 GB per file, unlimited files per project",
        storagePoint2: "Resumable — picks up exactly where it stopped",
        storagePoint3: "Every version kept for 30 days",
        uploading: "Uploading · 32%",
        resumable: "Resumable",
        videoTitle: "Private video previews",
        videoChip: "Signed",
        videoBadge: "No download",
        videoBody:
          "Your client presses play in the browser and reviews the cut in seconds — watermarked, streamed, never downloadable. The master stays locked until you're paid.",
        videoPoint1: "Signed links that expire on their own",
        videoPoint2: "Watermarked playback, downloads disabled",
        videoPoint3: "Instant streaming on any connection",
        videoFoot: "Previews stream from the same 40 GB delivery — nothing extra to upload.",
      },

      signature: {
        eyebrow: "Talk it through",
        title: "One tap. You're on the call.",
        subtitle:
          "Encrypted audio and video that starts inside the project — no meeting links, no phone numbers, no third-party app in the middle of your work.",
        videoTitle: "Private video previews",
        videoChip: "Signed",
        videoBadge: "No download",
        videoBody:
          "Your client presses play in the browser and reviews the cut in seconds — watermarked, streamed, never downloadable. The master stays locked until you're paid.",
        videoPoint1: "Signed links that expire on their own",
        videoPoint2: "Watermarked playback, downloads disabled",
        videoPoint3: "Instant streaming on any connection",
        videoFoot: "Previews stream from the same 40 GB delivery — nothing extra to upload.",
        callTitle: "Audio & video calls",
        callChip: "Encrypted",
        callLive: "Connected · 04:12",
        callYou: "You",
        callClient: "Client",
        callBody:
          "Ring the other side straight from the project chat. Both identities are already verified, so there is nothing to schedule and nothing to share — just talk, then get back to the work.",
        callPoint1: "Ring from the chat, connected in a second",
        callPoint2: "Verified people only — no strangers, no links",
        callPoint3: "HD audio or video, share your screen full size",
        callFoot: "Calls stay inside Lumvio — never routed through someone else's app.",
      },

      how: {
        eyebrow: "How subscribing pays off",
        title: "Verified → Invoiced → Paid, protected.",
        subtitle:
          "A three-step flow that turns your identity into leverage: a verified badge, premium global invoicing, and protected milestone payments.",
        verifyTitle: "Verified Identity Gate",
        verifyBody:
          "Pass the identity gate once. Your account earns a permanent verified badge — and one free project on us.",
        invoiceTitle: "Premium Global Invoicing",
        invoiceBody: "Bill clients anywhere. Accept crypto or Stripe cards — one invoice, multiple rails.",
        escrowTitle: "Protected Milestone Payments",
        escrowBody:
          "Client funds are held the moment they accept. Deliver the work, then the release process starts — with a clear record for both sides.",
        invoiceNumber: "Invoice #0142",
        due: "Due upon receipt",
      },
      security: {
        eyebrow: "Bank-Grade Identity Verification · Powered by Stripe Identity",
        titlePrefix: "Identity-verified",
        titleEmphasis: "freelancers only.",
        subtitle:
          "Every freelancer passes a 3-step identity gate before they can list a single project. Your clients see a green badge — you see peace of mind.",
        step1: "Step 01",
        step2: "Step 02",
        step3: "Step 03",
        scanTitle: "Government ID Scan",
        scanBody:
          "Passport, national ID, or driver's license — captured through Stripe's hosted secure flow. Never stored on Lumvio servers.",
        documentAccepted: "Document Accepted",
        passport: "Passport",
        nationalId: "National ID",
        driverLicense: "Driver License",
        encrypted: "Bank-grade encryption",
        biometricTitle: "Live Selfie Match",
        biometricBody:
          "Stripe's biometric engine runs a live liveness check and matches the selfie against the ID in real time.",
        livePassed: "Live Detection Passed",
        openMouth: "Liveness challenge",
        turnHead: "Face-to-ID match",
        aiScan: "Real-time biometric scan",
        spoofProof: "Advanced spoof-detection controls",
        badgeTitle: "Verified Badge Unlocked",
        badgeBody:
          "Stripe Identity confirms the match. Your profile earns a permanent green badge, and clients can hire with confidence.",
        badgeActive: "Badge Active",
        permanent: "Permanent on profile",
        trustBoost: "Client trust boost",
        fraudProof: "Identity fraud controls",
        geminiVerified: "Stripe Identity verified",
      },
      featuresEyebrow: "Everything you need",
      featuresTitle: "Built for real freelance work",
      featuresSubtitle:
        "Move from brief to paid deliverable inside a single trusted workspace — built for freelancers who ship serious work.",
      features: [
        {
          title: "Project pipeline",
          body: "Brief → In Progress → In Review → Completed. Always visible to both sides.",
        },
        {
          title: "File delivery",
          body: "Drop images, videos, and design files for review in one click.",
        },
        {
          title: "Client feedback",
          body: "Structured comments pinned to every file — no more email ping-pong.",
        },
        {
          title: "Global payouts",
          body: "Get paid your way — worldwide, with payment protection on each milestone.",
        },
      ],
      pricing: {
        eyebrow: "Pricing",
        title: "Simple plans. Serious results.",
        subtitle:
          "Start free, upgrade when you're ready. Every plan includes verified identity, protected payments, and a professional client experience.",
        popular: "Most popular",
        chipEscrow: "Payment protected",
        chipNoCommission: "0% commission on earnings",
        chipCancel: "Cancel anytime",
        freeForever: "Free forever for your first client",
        perProject: "≈ ${{value}} per project",
        perProjectUnlimited: "No project cap",
        perProjectFree: "1 project included",
      },
      trustFacts: {
        eyebrow: "Trust",
        title: "Why your money and your ID are safe here",
        subtitle: "Only facts we can prove today. No testimonials, no ratings, no logos we haven't earned.",
        items: [
          {
            title: "0% platform commission",
            body: "You keep 100% of what your client pays. Lumvio earns from subscriptions only — never from your invoices.",
          },
          {
            title: "Stripe Identity verification",
            body: "Every freelancer account is verified through Stripe Identity before a single project can be created.",
          },
          {
            title: "ID documents never stored on Lumvio",
            body: "Your passport or ID is submitted directly to Stripe. Lumvio stores the verification result, not your documents.",
          },
          {
            title: "Payment held until delivery is accepted",
            body: "The client funds the invoice up front. The money is released only after the delivery is accepted.",
          },
        ],
      },

      plans: [
        {
          name: "Free",
          price: "$0",
          period: "one time",
          desc: "Try the platform with your first client — no commitment required.",
          features: [
            "1 project after ID verification",
            "Verified freelancer badge",
            "Client feedback & file delivery",
            "Protected payments",
          ],
          cta: "Start free",
        },
        {
          name: "Starter",
          price: "$9.99",
          period: "/mo",
          desc: "Perfect for freelancers building a steady stream of work.",
          features: ["5 projects per month", "Global invoicing via Stripe", "Standard payout release", "Email support"],
          cta: "Get Starter",
        },
        {
          name: "Pro",
          price: "$19.99",
          period: "/mo",
          desc: "Built for freelancers managing multiple clients at scale.",
          features: [
            "15 projects per month",
            "Premium invoicing & receipts",
            "Priority payout release",
            "Priority email support",
          ],
          cta: "Get Pro",
        },
        {
          name: "Unlimited",
          price: "$34.99",
          period: "/mo",
          desc: "For studios, agencies, and teams that never want to slow down.",
          features: [
            "Unlimited projects per month",
            "Everything in Pro",
            "Team roles & shared workspace",
            "Priority support",
          ],
          cta: "Get Unlimited",
        },
      ],
      preview: {
        sample: "Example workspace",
        role: "Product Designer · Remote",
        verified: "ID verified",
        projects: "Projects",
        rating: "Rating",
        onTime: "On-time",
        monthlyEarnings: "Monthly earnings",
        paid: "Paid",
        escrow: "Payment Protection",
        tabs: {
          overview: "Overview",
          projects: "Projects",
          invoices: "Invoices",
          wallet: "Wallet",
        },
        live: "Live",
        illustrative: "Illustrative preview",
        months: ["Jan", "Mar", "May", "Jul", "Sep", "Nov"],
        payWithStripe: "Pay with Stripe",
        fundsSecured: "Funds Secured",
      },
      heroCtaSeats: "Claim your free seat — {{count}} left",
      heroCtaSeatsPlain: "Claim your free seat",
      securedByStripe: "Payments secured by Stripe",
      bento: {
        callConnected: "Connected",
        chipSize: "40 GB",
        chipResumable: "Resumable",
        chipDays: "30 days",
      },
      finalCtaTitle: "Verify in 60 seconds. Ship for years.",
      finalCtaSubtitle:
        "Freelancers pass the identity gate, then unlock a real client portal — payment protection, feedback, delivery, and crypto payouts, all included.",
      footer: "Verified freelance workspace.",
      roleSelector: {
        title: "How do you want to use Lumvio?",
        subtitle: "Pick your path. You can always switch later from settings.",
        freelancerBody: "Find clients, deliver work, and get paid through protected payments.",
        clientBody: "Post projects, review deliverables, and pay securely.",
        haveAccount: "Already have an account?",
      },
      socialCard: {
        eyebrow: "Community",
        title: "Connect with Lumvio",
        subtitle: "Join the community for product updates, freelance tips, and behind-the-scenes builds.",
        cta: "Follow us",
      },
      faq: {
        helpTitle: "Still have a question?",
        helpBody: "Our team answers every message. Ask about verification, payouts or payment protection before you sign up.",
        helpCta: "Talk to us",
        eyebrow: "Questions & answers",
        title: "Everything you need to know",
        subtitle: "Straight answers about identity, payments, and how Lumvio helps protect both sides.",
        items: [
          {
            q: "What makes Lumvio different from other freelance platforms?",
            a: "Every freelancer is identity-verified through Stripe Identity before they can list work, which helps keep fake and ghost accounts off the platform.",
          },
          {
            q: "How does identity verification work?",
            a: "You upload a government-issued ID and take a quick live selfie. Stripe's biometric engine confirms the match in seconds. Once approved, you earn a permanent verified badge on your profile.",
          },
          {
            q: "How do payments and payment protection work?",
            a: "Clients fund an invoice before work begins. Funds are held under payment protection until the deliverable is approved. Then the freelancer can withdraw via Stripe or crypto.",
          },
          {
            q: "Is there a free plan?",
            a: "Yes. Complete identity verification and unlock one free project to try the platform. Upgrade anytime when you're ready for more volume.",
          },
          {
            q: "Can I be both a freelancer and a client?",
            a: "Absolutely. You choose your primary role during signup and can switch between dashboards from your settings at any time.",
          },
          {
            q: "What happens if a client doesn't release payment?",
            a: "Payment protection covers both sides. If a client goes silent, funds auto-release to the freelancer after the review window ends — no chasing required.",
          },
        ],
      },
      howTitle: "How Lumvio works",
      step1Title: "Verify your identity",
      step1Body: "Complete a one-time ID verification check. Your verified badge follows you on every project.",
      step2Title: "Invite clients",
      step2Body: "Share a project link. Clients see a clean review workspace — no account required to browse.",
      step3Title: "Get paid safely",
      step3Body: "Protected invoices with clear steps. Withdraw to Binance Pay or crypto.",
    },
    auth: {
      captchaRequired: "Please complete the security check to continue.",
      captchaFailed: "Security check failed. Please refresh and try again.",
      welcomeTitle: "Welcome",
      welcomeSubtitle: "Sign in or create an account to continue.",
      backHome: "Back home",
      quote: "The clearest way I've ever collaborated with my clients. Feedback and files, finally in one place.",
      quoteBy: "Freelance designer using Lumvio",
      signInTitle: "Welcome back",
      signInSubtitle: "Sign in to your Lumvio account",
      signUpTitle: "Create your account",
      signUpSubtitle: "Start with a free verified freelancer profile",
      forgotPassword: "Forgot password?",
      signingIn: "Signing in…",
      toastSignedIn: "Signed in",
      confirmPassword: "Confirm password",
      showPassword: "Show password",
      hidePassword: "Hide password",
      passwordNotMet: "Password doesn't meet all requirements",
      passwordMismatch: "Passwords don't match",
      acceptTermsError: "You must accept the terms to continue",
      accountCreated: "Account created — you can sign in now.",
      termsPrefix: "I agree to the",
      terms: "Terms of Service",
      and: "and",
      privacy: "Privacy Policy",
      passwordRules: {
        len: "At least 8 characters",
        upper: "One uppercase letter (A–Z)",
        lower: "One lowercase letter (a–z)",
        num: "One number (0–9)",
        sym: "One symbol (!@#$…)",
      },
      strength: { label: "Strength:", weak: "Weak", fair: "Fair", good: "Good", strong: "Strong" },
      resetTitle: "Reset your password",
      resetDescription: "We'll email you a secure link to set a new password.",
      checkInbox: "Check your inbox",
      resetSentPrefix: "We sent a reset link to",
      resetSentSuffix: "The link expires in 1 hour.",
      resetSentToast: "Password reset email sent",
      sending: "Sending…",
      sendResetLink: "Send reset link",
      noAccount: "Don't have an account?",
      haveAccount: "Already have an account?",
      emailExists: "This email already has an account.",
      goSignIn: "Go to sign in",
      noAccountWithThisEmail: "No account found for {{email}}.",
      accountWasDeleted: "The account for {{email}} was permanently deleted. To use Lumvio again, please sign up for a new account.",
      goSignUpDeleted: "Create a new account",
      goSignUp: "Go to sign up",

      role: "I am a",
      freelancer: "Freelancer",
      client: "Client",
      badge: "Secure workspace access",
      headline: "Welcome back to Lumvio",
      otpNote: "We'll email you an 8-digit code after your password is verified.",
      trustLine: "Payment protected · Stripe Identity verified · Encrypted in transit and at rest",
      continueWithGoogle: "Continue with Google",
      or: "or",
      googleError: "Google sign-in failed. Please try again.",
    },

    sidebar: {
      projectsUsed: "{{used}}/{{limit}} projects used",
      projectsThisMonth: "{{used}} projects this month · Unlimited",
      plan: "{{name}} Plan",
      planSuffix: "{{name}} plan",
      freePlan: "Free plan",
      accountDetails: "Account details",
      nationality: "Nationality",
      idVerification: "ID verification",
      verified: "Verified",
      notVerified: "Not verified",
    },
    pricingPage: {
      badge: "Pricing",
      title: "Plans that grow with your client work",
      subtitle:
        "Start free with Payment Protection included. Upgrade the moment you land more clients. Cancel anytime.",
      popular: "Most popular",
      current: "Current",
      oneTime: "one time",
      freeForever: "Free forever for your first client",
      perProject: "≈ ${{value}} per project",
      perProjectUnlimited: "No project cap",
      perProjectFree: "1 project included",
      monthly: "/mo",
      footnote: "Prices in USD. Billed monthly via Stripe, PayPal or BaridiMob. 0% commission on your earnings.",
      verifyFirst: "Verify identity first",
      currentPlan: "Your current plan",
      managePlan: "Manage plan",
      upgradeToStarter: "Upgrade to Starter",
      included: "Included by default",
      redirecting: "Redirecting…",
      upgradeTo: "Upgrade to {{name}}",
      kyc: {
        title: "Verify your identity to activate any plan",
        body: "New freelancers must complete ID verification before starting on the Free plan or upgrading.",
        cta: "Verify identity",
      },
      toast: {
        verifyFirst: "Verify your identity first",
        checkoutStartFailed: "Could not start checkout",
        checkoutFailed: "Checkout failed",
      },
      plans: {
        free: {
          name: "Free",
          tagline: "Try the full workspace on your first client.",
          features: ["1 project total", "Payment Protection on every milestone", "Client portal, chat & files"],
        },
        starter: {
          name: "Starter",
          tagline: "For freelancers landing steady work.",
          features: ["5 projects / month", "Priority email support", "Standard payout release"],
        },
        pro: {
          name: "Pro",
          tagline: "For pros running a full client pipeline.",
          features: ["15 projects / month", "Advanced analytics", "Faster payout release"],
        },
        unlimited: {
          name: "Unlimited",
          tagline: "For agencies and high-volume freelancers.",
          features: ["Unlimited projects / month", "Dedicated support", "Team seats (soon)"],
        },
      },
    },
    plansPage: {
      title: "Plans & billing",
      subtitle:
        "Choose the plan that fits your client workload. Payment Protection and 0% commission are included on every plan.",
      chipCommission: "0% commission on earnings",
      chipProtection: "Payment Protection included",
      chipCancel: "Cancel anytime",
    },
    subscriptionCheckout: {
      title: "Checkout",
      metaTitle: "Checkout — Lumvio",
      loading: "Loading…",
      freelancerOnly: "Subscriptions are for freelancers",
      freelancerOnlyDesc: "Your client account is free — no plan needed.",
      backToPlans: "Back to plans",
      securedByStripe: "Secured by Stripe",
      subscribeTo: "Subscribe to",
      perMonth: "/ month",
      subtotal: "Subtotal",
      billing: "Billing",
      monthlyCancelAnytime: "Monthly, cancel anytime",
      dueToday: "Due today",
      stripeSecured: "Stripe secured",
      cancelAnytime: "Cancel anytime",
      noHiddenFees: "No hidden fees",
      paymentDetails: "Payment details",
      signedInAs: "Signed in as",
      alreadyActiveTitle: "You're on Lumvio {{plan}}",
      accessEnds: "Access ends {{date}}.",
      renews: "Renews {{date}} · Cancel anytime.",
      viewInvoices: "View invoices",
      changePlan: "Change plan",
      managePaymentMethod: "Manage payment method",
      transferSubmitted: "Transfer submitted",
      transferSubmittedDesc:
        "Your BaridiMob transfer for {{plan}} is awaiting admin review. We'll activate your plan within 24 hours and email you once approved.",
      submittedAt: "Submitted {{date}}",
      backToDashboard: "Back to dashboard",
      refreshStatus: "Refresh status",
      payWithCard: "Card",
      payWithBaridiMob: "BaridiMob",
      failedToStart: "Failed to start checkout",
      tryAgain: "Try again",
      preparingCheckout: "Preparing secure checkout…",
      paymentSuccessful: "Payment successful",
      activatingPlan: "Activating your plan…",
      processing: "Processing…",
      orPayWithCard: "Or pay with card",
      subscribePay: "Subscribe — securely pay",
      termsAgree: "By subscribing you agree to Lumvio's Terms. You can cancel anytime from Billing.",
      mandate:
        "You authorize Lumvio to charge your payment method on a recurring basis until you cancel. Cancel anytime from Billing — no questions asked.",
      encrypted: "Encrypted by Stripe · PCI-DSS Level 1",
      welcomePlan: "Welcome to Lumvio {{plan}}!",
    },
    settings: {
      title: "Profile & payment settings",
      subtitle: "Manage how you appear to clients and how you get paid.",
      profilePhoto: "Profile photo",
      profilePhotoHelp:
        "Clients will see your photo on every project. Once your identity is verified, a checkmark badge is added automatically.",
      changePhoto: "Change photo",
      uploadPhoto: "Upload photo",
      uploading: "Uploading…",
      remove: "Remove",
      fullName: "Full name",
      name: "Name",
      saving: "Saving…",
      changePassword: "Change password",
      changePasswordHelp: "Set a new password for your account. You'll stay signed in on this device.",
      newPassword: "New password",
      confirmPassword: "Confirm password",
      updatePassword: "Update password",
      updating: "Updating…",
      atLeast8: "At least 8 characters",
      repeatNewPassword: "Repeat new password",
      identityVerification: "Identity verification",
      fullNameLockedWhen:
        "Your name matches your verified ID and can no longer be changed. If your ID name is different, contact support to reset verification, then update your name and verify again.",
      fullNameHelp:
        "This is the name clients see. It must match the name on the ID you'll upload during verification. Once verified, it becomes permanent.",
      locked: "Locked",
      saveName: "Save name",
    },
    status: {
      brief: "Brief",
      in_progress: "In Progress",
      in_review: "In Review",
      completed: "Completed",
      stagePrefix: "Stage:",
    },
    legalLayout: {
      eyebrow: "Legal",
      lastUpdated: "Last updated",
      backHome: "Back to home",
    },
    aboutPage: {
      title: "About Lumvio",
      updated: "July 30, 2026",
      intro:
        "Lumvio is the verified-freelancer workspace. We combine identity verification, protected milestone payments, real-time chat, and branded invoicing so independent freelancers can win, deliver, and get paid for global work without the friction of chasing payments or negotiating trust.",
      missionHeading: "Our mission",
      missionBody:
        "Make independent work as safe and legitimate as any full-time job. Every freelancer is Stripe Identity verified, every payment is held under payment protection until the client approves the deliverable, and every conversation lives in a project workspace built to protect both sides.",
      diffHeading: "What makes Lumvio different",
      diffList: [
        "**Verified only:** freelancers pass Stripe Identity before onboarding, which helps keep fake profiles out.",
        "**Payment protection by default:** client funds are held before work begins and released on approval.",
        "**Global payouts:** receive funds in your local currency through country-aware payout rails.",
        "**3-device account lock:** a hard limit that prevents account sharing and credential theft.",
      ],
      contactHeading: "Get in touch",
      contactBody: "Questions, partnerships, or press inquiries:",
    },
    faqPage: {
      title: "Frequently Asked Questions",
      updated: "July 30, 2026",
      items: [
        {
          q: "Do I need to verify my identity to sign up?",
          a: "No — you can create an account and browse for free. Freelancers only need to pass Stripe Identity verification before creating their first project.",
        },
        {
          q: "Does Lumvio take a commission from my earnings?",
          a: "No. Lumvio never takes a commission from freelancer earnings. Optional subscription plans unlock additional monthly projects and premium features. Payments are processed by Stripe, so their standard payment-processing fee applies to each transaction.",
        },
        {
          q: "How does payment protection work?",
          a: "Clients pay upfront, funds are held under payment protection, and released to the freelancer only after the client approves the delivered work.",
          linkLabel: "See the Payment Protection Policy for details.",
          linkTo: "/payment-protection-policy",
        },
        {
          q: "What payment methods are supported?",
          a: "Clients pay via Stripe (Visa, Mastercard, and other major cards). Freelancers withdraw through country-specific rails such as bank transfer and local mobile wallets.",
        },
        {
          q: "Why am I limited to 3 devices?",
          a: "To protect your account from unauthorized sharing.",
          linkLabel: "Read the full Device & Account Security Policy.",
          linkTo: "/device-policy",
        },
        {
          q: "Can I get a refund?",
          a: "Yes — refunds are handled through our dispute process.",
          linkLabel: "See the Refund Policy.",
          linkTo: "/refund",
        },
      ],
    },
    helpPage: {
      title: "Help Center",
      updated: "July 30, 2026",
      intro:
        "The fastest way to get help on Lumvio is to browse the topics below or email us at {{email}}. We typically respond within one business day.",
      gettingStartedHeading: "Getting started",
      gettingStarted: [
        "Create an account and choose your role (freelancer or client).",
        "Freelancers: pass Stripe Identity verification before creating your first project.",
        "Set up your payout method in Settings → Payouts (freelancers only).",
      ],
      paymentsHeading: "Payments & payment protection",
      paymentsBody: "Funds are held under payment protection until the client approves the deliverable.",
      paymentsLink: "See our Refund & Payment Protection Policy for full details.",
      deviceHeading: "Account & devices",
      deviceBody: "Each account is limited to 3 registered devices.",
      deviceLink: "Read the full Device & Account Security Policy.",
      contactHeading: "Contact support",
      contactBody:
        "Email {{email}} with your account email and a short description of the issue. Screenshots help us resolve tickets faster.",
    },
    devices: {
      title: "Your devices",
      subtitleFreelancer: "Devices that have signed in to your Lumvio account.",
      subtitleOther: "Recent sign-in activity for your Lumvio account. No device limit applies to your role.",
      locked: "devices (locked)",
      activeUnlimited: "active · unlimited",
      loading: "Loading…",
      empty: "No active devices found.",
      thisDevice: "This device",
      ipPrefix: "IP:",
      lastActive: "Last active {{when}}",
      securityBody:
        "For security and to prevent account sharing, your account is locked to 3 devices. To change your registered devices, please contact support at ",
      contactSupport: "Contact support",
    },
    dashboard: {
      widgets: {
        earnings: "Earnings",
        spend: "Spend",
        last30Days: "last 30 days",
        escrowReleases: "Payment Protection releases",
        autoReleaseInProgress: "Auto-release in progress",
        fundsHeldUntilRelease: "Funds held until release",
        noPendingReleases: "No pending releases",
        releasing: "Releasing…",
        leftSuffix: "left",
        days: "d",
        hours: "h",
        minutes: "m",
        project: "Project",
        recentActivity: "Recent activity",
        recentActivitySubtitle: "Messages, invoices and payment protection events",
        nothingYet: "Nothing yet",
        nothingYetBody: "New updates land here in realtime.",
        liveRealtime: "Live · realtime",
        showLess: "Show less",
        showAll: "Show all",
        totalSuffix: "total",
        noMessagesYet: "No messages yet",
        you: "You",
        quick: {
          wallet: "Wallet",
          walletHint: "Withdraw",
          invoices: "Invoices",
          invoicesHint: "Track",
          invoicesPayHint: "Pay & release",
          verify: "Verify",
          verifyHint: "Identity",
          upgrade: "Upgrade",
          upgradeHint: "Plans",
          projects: "Projects",
          projectsHint: "Chat",
          settings: "Settings",
          settingsHint: "Profile",
        },
      },
      freelancer: {
        projectsTitle: "Projects",
        projectsSubtitle: "Manage your client work and deliveries.",
        newProject: "New project",
        activeSection: "Active projects",
        completedSection: "Completed",
        emptyTitle: "No active projects yet",
        emptyBody: "Create your first project to get started.",
        walletTitle: "Wallet & payouts",
        walletBody: "Net after Stripe fees · Request withdrawals to your locked wallet",
        stats: {
          active: "Active",
          inReview: "In review",
          earned: "Earned (net)",
          inEscrow: "Protected",
        },
        create: {
          title: "Create new project",
          projectTitle: "Project title",
          projectTitlePh: "e.g. Brand identity refresh",
          description: "Description",
          descriptionPh: "Short brief for your client…",
          clientEmail: "Client email",
          clientEmailPh: "client@company.com",
          budget: "Budget ($)",
          budgetPh: "5000",
          submit: "Create project",
          success: "Project created",
        },
        row: {
          noDescription: "No description",
        },
        banner: {
          kycRejectedTitle: "Identity verification failed",
          kycRejectedBody: "The face in the live camera did not match your ID. Try again.",
          kycPendingTitle: "Verification in progress",
          kycPendingBody: "Freelancers must complete a quick ID + liveness check before receiving crypto payouts.",
          kycStartTitle: "Verify your identity to receive payouts",
          kycStartBody: "Freelancers must complete a quick ID + liveness check before receiving crypto payouts.",
          retryVerify: "Retry verification",
          startVerify: "Start verification",
          freeLimitTitle: "Free plan used — locked",
          freeLimitBody: "You've used your 1 free project. Upgrade to a paid plan to open more.",
          upgradeCta: "Upgrade to unlock",
        },
        upgrade: {
          kycTitle: "Verify your identity to unlock 1 free project",
          duplicateTitle: "This identity already used the free plan",
          limitTitle: "You've reached your monthly project limit",
          kycBodyPart1: "Every freelancer must pass a quick ID + liveness check before creating their first project.",
          kycBodyPart2: "Once verified, your ",
          kycBodyEmphasis: "1 free project",
          kycBodyPart3: " unlocks instantly.",
          duplicateBody:
            "The ID document you verified with has already claimed the free plan on another account. To keep using Lumvio with this identity, please upgrade to a paid plan.",
          limitBodyPart1: "Your ",
          limitBodyPart2: " plan includes ",
          limitProjectsUnlimited: "unlimited projects",
          limitProjectsN: "{{count}} projects",
          limitBodyMonth: " per month",
          limitBodyEnd: ". Upgrade to keep onboarding clients smoothly.",
          kycBullet1: "60-second ID scan",
          kycBullet2: "Liveness face check",
          kycBullet3: "Unlock 1 free project instantly",
          planBullet1: "More projects per month",
          planBullet2: "Priority support & faster payouts",
          planBullet3: "Cancel anytime",
          verifyCta: "Verify identity",
          plansCta: "See plans",
        },
      },
      client: {
        welcome: "Welcome back",
        welcomeName: "Welcome back, {{name}}",
        subtitle: "Track your projects, review files, and share feedback.",
        unpaidOne: "1 invoice waiting for payment",
        unpaidMany: "{{count}} invoices waiting for payment",
        unpaidBody: "Review and pay securely via Stripe.",
        emptyTitle: "No projects assigned to you yet",
        emptyBody: "When your freelancer adds you to a project using {{email}}, it will appear here.",
        activeSection: "Active",
        completedSection: "Completed",
        openLabel: "Open",
      },
    },
    verify: {
      pageBadge: "Secure ID gate",
      title: "Verify your identity",
      intro:
        "Takes about a minute. Required once — then your account is cleared to receive payouts and unlock 1 free project.",
      verifiedTitle: "You're verified",
      verifiedBody: "Your identity has been confirmed. You're cleared to receive payouts.",
      verdict: {
        okTitle: "Identity verified",
        refusedTitle: "Verification refused",
        failedTitle: "Verification failed",
        okBody: "Your ID matches your live photo. You're cleared to receive payouts.",
        duplicateBody:
          "This ID document is already verified on another Lumvio account. Each identity can only verify one account.",
        failedBody: "The face in the live camera does not match the photo on the provided ID.",
        confidence: "AI confidence:",
        nextSteps: "Next steps",
        dupTip1: "Sign in to the account that originally verified this ID.",
        dupTip2: "Lost access? Contact support to release the identity.",
        dupTip3: "Never upload another person's document — it will be refused.",
        contactSupport: "Contact support",
      },
      stepLabel: "Step",
      step1: {
        title: "Where's your document from?",
        subtitle: "Pick the country that issued your ID and which document you'll upload.",
        country: "Country of issue",
        selectCountry: "Select a country…",
        docType: "Document type",
      },
      docs: {
        passport: "Passport",
        passportHint: "Photo page with your name, DOB & face",
        id_card: "National ID card",
        id_cardHint: "Front side, must show your face clearly",
        drivers_license: "Driver's licence",
        drivers_licenseHint: "Front side with photo & full name",
      },
      step2: {
        title: "Upload your document",
        subtitle: "Sharp, well-lit photo. Whole document in frame, face clearly visible.",
        subtitleLocked: "Choose your country and document type first.",
        click: "Click to upload",
        hint: "PNG, JPG · up to 8 MB",
        uploading: "Uploading…",
        uploaded: "Document uploaded",
        tooBig: "Image must be under 8 MB",
        uploadFailed: "Upload failed",
      },
      step3: {
        title: "Liveness face check",
        subtitle: "Prove you're a real, live person right now.",
        waitDoc: "Upload your document first to enable the camera.",
        captured: "Live photo captured",
        uploadingCapture: "Uploading capture…",
        intro:
          "When ready, press start and open your mouth, then turn your head left and right. We'll capture the frame automatically.",
        liveUploadFailed: "Live upload failed",
      },
      submit: "Submit for AI review",
      submitting: "Verifying with AI…",
      chooseFirst: "Choose your country and document type first",
      bothPhotos: "Both photos are required",
      successToast: "Identity verified",
      failToast: "Verification failed",
      duplicate: {
        title: "Verification refused",
        body: "This ID document is already verified on another account. Upload a different, valid government ID that belongs to you and try again.",
        beforeRetry: "Before you retry",
        tip1: "Use your own government-issued ID.",
        tip2: "Make sure the document is clear, well-lit, and unedited.",
        tip3: "Do not upload someone else's document — it will be refused again.",
      },
    },
    liveness: {
      loading: "Loading face detection…",
      start: "Start liveness check",
      capturedNote: "Live face captured. Ready to submit.",
      center: "Center your face in the frame",
      openWider: "Open your mouth wider…",
      step1: "Step 1 of 3 — Open your mouth wide",
      step2: "Step 2 of 3 — Slowly turn your head to the LEFT",
      step3: "Step 3 of 3 — Now turn your head to the RIGHT",
      great: "Great!",
      cameraFail: "Camera or model failed to load",
    },
    wallet: {
      title: "Wallet",
      subtitle: "Track your earnings after Stripe fees and request payouts to your locked wallet.",
      available: "Available balance",
      withdraw: "Withdraw",
      requestWithdrawal: "Request withdrawal",
      history: "Withdrawal history",
      historyLabel: "WITHDRAWAL HISTORY",
      empty: "No withdrawal requests yet.",
      stats: {
        requestedPaid: "REQUESTED / PAID",
        releasedToYou: "RELEASED TO YOU",
        available: "AVAILABLE",
        requestedPaidHint: "Pending + approved + paid withdrawals",
        releasedToYouHint: "Sum of paid invoices, net of Stripe fees",
        availableHint: "Ready to withdraw",
      },
      locked: "LOCKED",
      lockedWallet: "Locked payout wallet",
      lockedHelp: "Your wallet is locked for security. Contact support to change it.",
    },
    invoices: {
      title: "Invoices",
      subtitle: "Generate invoices for your projects and track payment.",
      newInvoice: "New invoice",
      empty: "No invoices yet.",
      badgeLive: "Invoices · Live",
      subtitleFreelancer:
        "From draft to payment protection to payout. Every invoice, tracked in one precise workspace.",
      subtitleClient: "Pay, review and release funds securely. Everything stays protected until you approve.",
      noMatch: "No invoices match this filter.",
      searchPlaceholder: "Search invoices…",
      untitled: "Untitled project",
      currency: { usd: "USD", symbol: "$" },
      status: {
        pending: "Pending",
        paid: "Paid",
        inEscrow: "Protected",
        released: "Released",
      },
      filters: {
        all: "All",
        pending: "Pending",
        inEscrow: "Protected",
        released: "Released",
      },
      stats: {
        totalInvoiced: "Total invoiced",
        totalBilled: "Total billed",
        invoicesOne: "{{count}} invoice",
        invoicesOther: "{{count}} invoices",
        pending: "Pending",
        awaitingPayment: "{{count}} awaiting payment",
        inEscrow: "Protected",
        heldSafely: "{{count}} held safely",
        paidOut: "Paid out",
        released: "Released",
        completed: "{{count}} completed",
      },
      feeCard: {
        title: "Card processing fee",
        body: "When clients pay with a card, a 2.9% + 30¢ processing fee is deducted from the invoice amount. You receive the remainder in your payment protection and available balance.",
      },
      dialog: {
        title: "Create invoice",
        subtitle: "Secured by payment protection — funds only release when your client approves.",
        project: "Project",
        selectProject: "Select a project",
        amount: "Amount",
        amountPlaceholder: "500.00",
        description: "Description",
        optional: "— optional",
        descriptionPlaceholder: "Milestone 1 — design mockups",
        footerNote: "Payment Protection protected · Stripe secured",
        submit: "Create invoice",
      },
      preview: {
        clientPays: "Client pays",
        cardFee: "Card processing fee",
        youReceive: "You receive",
      },
      row: {
        grossMinusFee: "Gross ${{gross}} − fee ${{fee}}",
        freelancerNets: "Freelancer nets ~${{net}}",
      },
      actions: {
        pay: "Pay",
        confirmRelease: "Confirm & release",
        inEscrow: "Protected",
        released: "Released",
        paid: "Paid",
      },
      countdown: {
        autoReleasing: "Auto-releasing…",
        clientLabel: "Auto-releases in {{h}}h {{m}}m if you don't act",
        freelancerLabel: "Auto-release in {{h}}h {{m}}m",
      },
      delete: {
        title: "Delete this invoice?",
        bodyWithAmount: "This will permanently remove the invoice for ${{amount}}. This cannot be undone.",
        bodyPlain: "This will permanently remove the invoice. This cannot be undone.",
        cancel: "Cancel",
        confirm: "Delete",
        deleting: "Deleting…",
      },
      edit: {
        title: "Edit invoice",
        amount: "Amount (USD)",
        description: "Description",
        cancel: "Cancel",
        save: "Save changes",
        saving: "Saving…",
      },
      checkout: {
        badge: "Secure checkout",
        title: "Complete payment",
        subtitle: "Your funds stay protected until you approve the delivery.",
        invoiceFallback: "Invoice",
        descFallback: "Freelance services",
        total: "Total",
        methods: "Card · Apple Pay · Google Pay",
        stripeNote: "Powered by Stripe — enter your details on Stripe's secure page.",
        tls: "256-bit TLS",
        pci: "PCI DSS Level 1",
        neverSeeCard: "We never see your card",
        breakdown: "Breakdown",
        youPay: "You pay",
        stripeFee: "Stripe fee ({{pct}}% + ${{fixed}})",
        freelancerReceives: "Freelancer receives",
        protectedTitle: "Protected by Lumvio Payment Protection.",
        protectedBody: "Funds are held safely and only released once you approve the work.",
        cancel: "Cancel",
        processing: "Processing…",
        pay: "Pay ${{amount}}",
      },
      toasts: {
        paymentReceived: "Payment received",
        created: "Invoice created",
        updated: "Invoice updated",
        deleted: "Invoice deleted",
        releasedToFreelancer: "Funds released to freelancer",
        selectProject: "Select a project",
        validAmount: "Enter a valid amount",
        checkoutFailed: "Checkout failed",
        noCheckoutUrl: "No checkout URL returned",
      },
    },
    escrow: {
      pageTitle: "Payment Protection · Lumvio",
      badge: "Protected by Lumvio Payment Protection",
      title: "Payment Protection",
      subtitle:
        "Funds move only when both sides are happy. Release early, extend the timer, or raise a dispute at any time.",
      stats: {
        held: "Held",
        disputed: "Disputed",
        released: "Released",
      },
      nextRelease: "Next auto-release",
      filters: {
        all: "All",
        inEscrow: "Protected",
        disputed: "Disputed",
        released: "Released",
      },
      searchPlaceholder: "Search projects…",
      emptyTitle: "No payment protection transactions yet",
      emptyBody: "Once a client pays an invoice, it will appear here.",
      liveUpdate: "Live-updating every minute",
      card: {
        protectedBy: "Protected by Lumvio Payment Protection",
        extended: "+{{hours}}h extended",
        partialReleased: "${{amount}} partially released",
        projectFallback: "Project",
        gross: "Gross",
        netToFreelancer: "Net to freelancer",
        remaining: "Remaining protected",
        actions: {
          releaseNow: "Release now",
          partialRelease: "Partial release",
          timer: "Timer",
          extend24h: "Extend 24h",
          releaseSooner: "Release 24h sooner",
          raiseDispute: "Raise dispute",
        },
        dispute: {
          title: "Raise a dispute",
          description:
            "The auto-release timer will freeze immediately. Our team will review and reach out. Please describe the issue in detail.",
          placeholder: "What went wrong? Include any relevant context…",
          cancel: "Cancel",
          open: "Open dispute",
          openSuccess: "Dispute opened. Payment Protection is frozen until resolved.",
          openError: "Could not open dispute",
          openReasonMin: "Please provide at least 8 characters.",
          status: "Dispute open",
          reasonLabel: "Reason",
        },
        partial: {
          title: "Release part of the payment protection",
          description: "You have ${{total}} remaining protected. Enter how much to release now — the rest stays held.",
          amountLabel: "Amount (USD)",
          placeholder: "e.g. 50.00",
          percent25: "25%",
          percent50: "50%",
          percent75: "75%",
          cancel: "Cancel",
          release: "Release ${{amount}}",
          releaseSuccess: "Partial release sent to freelancer.",
          releaseError: "Could not release partial funds",
        },
        releaseSuccess: "Funds released to freelancer.",
        releaseError: "Could not release funds",
        extendSuccess: "Timer updated — releases {{date}}.",
        extendError: "Could not update timer",
        status: {
          held: "Held protected",
          disputed: "Disputed",
          released: "Released",
          refunded: "Refunded",
        },
        timeline: {
          funded: "Funded",
          disputed: "Disputed",
          inEscrow: "Protected",
          released: "Released",
        },
        countdown: {
          released: "Released",
          refunded: "Refunded",
          frozen: "Frozen",
          releasing: "Releasing…",
          toRelease: "to release",
          onHold: "on hold",
          releasedEmpty: "",
        },
      },
      insights: {
        successRate: "Success rate",
        avgRelease: "Avg release time",
        active: "Active protected payments",
        successLabel: "Success",
      },
      timerWidget: {
        completed: "Completed",
        noDeadline: "No deadline",
        overdue: "Overdue",
        days: "d",
        hours: "h",
        minutes: "m",
        seconds: "s",
      },
      reviewPeriod: {
        badgeReleased: "Released",
        badgeReview: "Review period",
        fundsReleasedTitle: "Funds released successfully",
        fundsReleasedClient: "Payment has been sent to the freelancer. This project is now closed.",
        fundsReleasedFreelancer: "The client released the funds. Payment has been sent to your wallet.",
        windowEnded: "Review window ended",
        clientReviewing: "You have time to review this delivery",
        freelancerWaiting: "Client is reviewing your delivery",
        autoReleasing: "Funds are being released to the freelancer automatically.",
        approveHint: "Approve now to release funds instantly, or extend the timer if you need more time.",
        waitingHint: "Funds will auto-release when the timer ends unless the client extends it.",
        remaining: "remaining",
        approveRelease: "Approve & release now",
        extendLabel: "Extend:",
        extendMax: "Max 72h window reached",
        releasePartial: "Release partial amount",
        openDispute: "Open a dispute",
        extendedNote: "Extended by {{hours}}h of a 24h maximum (72h total window).",
        disputeTitle: "Open a dispute",
        disputeDescription:
          "Funds stay locked protected until our team reviews and resolves the dispute. Please describe the issue clearly.",
        disputePlaceholder: "What went wrong with the delivery?",
        disputeSubmit: "Open dispute",
        disputeToast: "Dispute opened. Our team will step in.",
        disputeErrorNoInvoice: "No invoice available to dispute",
        partialTitle: "Release a partial amount",
        partialDescription:
          "Send part of the payment protection to the freelancer now. The remainder stays locked until you release or dispute it.",
        partialAmount: "Amount (max ${{max}})",
        partialToast: "Partial release sent to the freelancer.",
        partialErrorNoInvoice: "No invoice available to release",
        extendedToast: "Review time extended by {{hours}}h",
        approvedToast: "Approved",
        releasedCountToast: "Released {{count}} invoice(s)",
        cancel: "Cancel",
      },
    },
    chat: {
      title: "Chat",
      back: "Back to project",
      conversationWith: "Conversation with {{name}}",
      empty: "No messages yet. Say hello.",
      emptyTitle: "Start the conversation",
      placeholder: "Write a message…",
      hint: "Press Enter to send · Shift+Enter for new line",
      hintSend: "to send",
      hintNewline: "for new line",
      openChat: "Chat",
      online: "Online now",
      directMessage: "Direct message",
      threads: "Conversations",
      search: "Search…",
      noThreads: "No conversations yet.",
      noResults: "No matches.",
      jumpLatest: "Jump to latest",
      newMessages: "New messages",
      typingNow: "Typing now…",
      freelancer: "Freelancer",
      viewPortfolio: "View portfolio",
      dayToday: "Today",
      dayYesterday: "Yesterday",
      pageSubClient: "Search a freelancer by username, view their profile and start a secure conversation.",
      pageSubFreelancer: "Share your profile link — clients who open it can start a chat with you in one click.",
      findFreelancer: "Find a freelancer",
      searchPlaceholder: "Search by username, name or skill…",
      noMatch: 'No freelancer matches "{{q}}". Try another username.',
      searchTip: "Tip: ask the freelancer for their @username and search it here.",
      startChat: "Start chat",
      view: "View",
      rate: "Rate",
      yourPublicProfile: "Your public profile",
      shareIntro: "Share this link with clients — they can view your work and start a chat with you instantly.",
      copy: "Copy",
      open: "Open",
      activatePortfolio: "Activate your portfolio to get a shareable @username link.",
      setupPortfolio: "Set up portfolio",
      copied: "Profile link copied",
      couldNotStart: "Could not start chat.",
      messages: "Messages",
      unread: "{{count}} unread",
      allCaughtUp: "You're all caught up",
      noClientMessages: "No client messages yet",
      noProjectMessages: "No project messages yet",
      all: "All",
      aria: {
        toggleContext: "Toggle context panel",
        addReaction: "Add reaction",
        removeAttachment: "Remove attachment",
        attachImage: "Attach image",
        attachFile: "Attach file",
      },
      context: {
        milestone: "Current milestone",
        noMilestone: "No milestones yet.",
        invoice: "Active invoice",
        noInvoice: "No invoices yet.",
        viewAll: "View all invoices",
        files: "Shared files",
        noFiles: "No files shared yet.",
      },
    },
    projectDetail: {
      largeFiles: {
        title: "Large file transfer",
        subtitleOwner:
          "Send final deliveries up to 40 GB. Files stay available while the project is active, then for 30 days after completion.",
        subtitleClient: "Upload your source material up to 40 GB, and download deliveries your freelancer sends.",
        uploadDelivery: "Upload delivery",
        uploadSource: "Upload source files",
        cancel: "Cancel upload",
        resuming: "Resuming interrupted upload…",
        uploadingPart: "Uploading {{done}} of {{total}} parts",
        completing: "Finalising and verifying…",
        empty: "No large files yet.",
        version: "Version {{n}}",
        fromOwner: "From your freelancer",
        fromClient: "From the client",
        available: "Available",
        pending: "Upload in progress",
        failed: "Upload failed",
        expiresOn: "Available until {{date}}",
        noExpiry: "Available while the project is active",
        getLink: "Get download link",
        linkReady: "Download link generated — valid for 15 minutes",
        linkRequested: "Download requested",
        requests: "{{count}} link requests",
        tooLarge: "That file is larger than the 40 GB limit.",
        generalTooLarge: "Attachments here are limited to 300 MB. Use large file transfer for bigger files.",
        uploadDone: "Upload complete and verified",
        uploadFailedToast: "Upload failed. You can resume it by choosing the same file again.",
        storageBlocked:
          "Your browser couldn't reach the storage service. Check your connection and pick the same file again to resume.",
        cancelled: "Upload cancelled",
        dropHere: "Drag a file here, or click to choose one",
        dropHint: "Up to 40 GB · resumable if the connection drops",
        dropActive: "Drop the file to start uploading",
        progressOf: "{{done}} of {{total}} uploaded",
        remaining: "{{size}} remaining",
        locked:
          "Uploads are paused because the project owner's subscription isn't active right now. Downloads of existing files still work.",
        preview: {
          create: "Create preview",
          preparing: "Preparing preview…",
          encoding: "Encoding preview {{pct}}%",
          watch: "Watch preview",
          retry: "Try preview again",
          failed: "Preview unavailable",
          removed: "Preview removed",
          started: "Preview started — this can take a few minutes for long videos.",
          createFailed: "We couldn't start the preview. The original file is untouched.",
          playFailed: "We couldn't open the preview. Please try again.",
          notVideo: "Previews are only available for video files.",
          tooLarge: "This video is too large to preview. You can still download it.",
          dialogHint: "Private streaming preview — the link expires shortly and can't be downloaded.",
        },
      },

      loading: "Loading…",
      notFound: "Project not found or you don't have access.",
      backToProjects: "Back to projects",
      client: "Client",
      price: "Price",
      duration: "Duration",
      deadline: "Deadline",
      workspace: "Workspace",
      statusUpdated: "Status updated",
      status: {
        brief: "Brief",
        in_progress: "In Progress",
        in_review: "In Review",
        completed: "Completed",
      },
      yourFreelancer: "Your freelancer",
      freelancerFallback: "Freelancer",
      yourFreelancerFallback: "your freelancer",
      identityVerified: "Identity verified",
      identityNotVerified: "Identity not verified",
      pipeline: { title: "Project pipeline", complete: "{{percent}}% complete" },
      clientAssets: {
        title: "Client assets",
        hintClient: "Share a link (Google Drive, Dropbox, WeTransfer…) with the materials your freelancer needs.",
        hintFreelancer: "The client can share their materials here via a link.",
        urlPlaceholder: "https://drive.google.com/…",
        notePlaceholder: "Optional note (access instructions, password, what's inside…)",
        sendLink: "Send link",
        saved: "Assets link saved",
        invalidUrl: "Link must start with http:// or https://",
        waiting: "Waiting for the client to share their assets link.",
      },
      files: {
        title: "Files",
        upload: "Upload",
        uploaded: "File uploaded",
        uploadFailed: "Upload failed",
        removed: "File removed",
        empty: "No files yet.",
        previewUnavailable: "Preview unavailable",
        reviewComment: "Review & comment",
        review: "Review",
        openReview: "Open review",
      },
      feedback: {
        title: "Feedback",
        one: "{{count}} comment",
        other: "{{count}} comments",
        general: "General project comment",
        onFile: "On file: {{name}}",
        placeholder: "Share feedback…",
        empty: "No comments yet.",
        on: "On: {{name}}",
      },
      completed: {
        badge: "Project completed",
        hasInvoiceTitle: "Invoice ready to review",
        noInvoiceTitle: "Nice work — time to get paid",
        hasInvoiceBody:
          "You already created an invoice for this project. Track its status or send a reminder to your client.",
        noInvoiceBody:
          "Send an invoice to {{email}} and Lumvio will handle secure payment protection until funds are released.",
        viewInvoice: "View invoice",
        createInvoice: "Create invoice",
      },
    },
    workspace: {
      titleSuffix: "Workspace",
      backToProject: "Back to project",
      description:
        "A shared Miro board for this project. Sticky notes, wireframes, and reviews stay in sync for everyone with access.",
      openInMiro: "Open in Miro",
      changeBoard: "Change board",
      attachBoard: "Attach Miro board",
      shareUrlLabel: "Miro board share URL",
      urlPlaceholder: "https://miro.com/app/board/uXj...",
      shareHint:
        'In Miro, click Share → set access to "Anyone with the link can view/edit", then paste the URL here. Only people assigned to this project can see it inside Lumvio.',
      saveBoard: "Save board",
      updated: "Miro board updated",
      invalidUrl: "Please paste a Miro board URL (miro.com/app/board/...).",
      loading: "Loading workspace…",
      notAvailable: "This workspace isn't available or you don't have access.",
      noBoardTitle: "No board attached yet",
      ownerNoBoard: "Attach a Miro board to give your client a live whiteboard for feedback and reviews.",
      clientNoBoard: "Your freelancer hasn't attached a Miro board to this project yet.",
      iframeTitle: "Miro board for {{title}}",
    },
    simulator: {
      eyebrow: "Try it live",
      titlePrefix: "See exactly where every ",
      titleAccent: "dollar",
      titleSuffix: " goes.",
      subtitle: "Pick a project value and follow the money through the three stages of a Lumvio contract.",
      projectValue: "Project value",
      breakdown: "Breakdown",
      clientPays: "Client pays",
      stripeFeeLabel: "Stripe fee",
      stripeFeeNote: "2.9% + $0.30",
      platformFee: "Lumvio platform fee",
      free: "Free",
      freelancerReceives: "Freelancer receives",
      guarantee: "Lumvio never takes a platform cut. You only pay Stripe's payment-processor rate.",
      flow: "How the money moves",
      step: "Step",
      step1Title: "Client pays",
      step1Body: "Card is charged securely via Stripe. Funds move instantly.",
      step2Title: "Held under protection",
      step2Body: "Money is locked. Freelancer starts work with confidence.",
      step3Title: "Released to freelancer",
      step3Body: "Auto-released after the review window. Ready to withdraw.",
      footnote:
        "Estimated Stripe online processing fee: 2.9% + $0.30 per transaction · Lumvio charges 0% commission on project payments · An eligible Lumvio plan is required",
    },
    pulse: {
      eyebrow: "Live · Trust pulse",
      protected: "Payment protected",
      delivered: "Projects delivered",
      rating: "Avg. member rating",
      seats: "Free seats remaining",
      tickerLabel: "Recent verified activity",
      tickerHint: "Names blurred for privacy",
      justNow: "just now",
      ticker: {
        verified: "Stripe Identity verified",
        delivered: "Project marked complete · {{count}} delivered this week",
        review: "New 5★ review · avg {{rating}}/5 across {{count}} reviews",
        joined: "Client joined from 🇫🇷 France · funded a protected payment",
        payout: "Payout confirmed to verified wallet · USDT",
        otp: "OTP-secured payout method change approved",
        released: "Contract auto-released after 24h review window",
      },
    },
    portfolioMedia: {
      defaultLabel: "Upload photo or short video",
      defaultHint: "PNG, JPG, WEBP, GIF · MP4, WEBM · up to 50 MB",
      uploading: "Uploading… {{progress}}%",
      chooseFile: "Choose file",
      camera: "Camera",
      orDropHere: "or drop it here",
      replace: "Replace",
      remove: "Remove",
      video: "Video",
      image: "Image",
      tooLarge: "File too large (max 50 MB)",
      signFailed: "Sign failed",
      uploadFailed: "Upload failed",
      uploaded: "Uploaded",
      dragToReorder: "Drag to reorder",
    },
    slotsBadge: {
      pillClaimed: "{{claimed}}/{{total}} free seats claimed",
      pillFull: "All {{total}} free seats taken",
      chipLimited: "Limited free launch",
      chipFull: "Free plan full",
      leftCount: "{{count}} left",
      cardCopy: "{{claimed}} of {{total}} verified freelancers claimed a free seat.",
      cardFull: "All {{total}} free seats have been claimed. Upgrade to a paid plan to continue.",
    },
    pricingIntro: {
      blurb:
        "Lumvio never takes a commission from freelancer earnings. Optional plans unlock additional projects and premium features. Only Stripe's standard payment-processing fee applies.",
      stripeSecured: "Stripe secured",
      cancelAnytime: "Cancel anytime",
      noHiddenFees: "No hidden fees",
    },
    escrowPreview: {
      stageBrief: "Brief",
      stageInProgress: "In Progress",
      stageInReview: "In Review",
      stageCompleted: "Completed",
      logoConcepts: "Logo concepts · Draft round",
      chatQuestion: "Can we try the logo 10% larger?",
      chatReply: "On it — v2 in 20 min",
      typing: "Alex is typing…",
      nextPayout: "Next payout",
      payWithStripe: "Pay with Stripe",
      fundsSecured: "Funds Secured",
    },
    calls: {
      audioCall: "Audio call",
      videoCall: "Video call",
      startAudio: "Start audio call",
      startVideo: "Start video call",
      calling: "Calling…",
      ringing: "Ringing…",
      incoming: "Incoming call",
      incomingAudio: "Incoming audio call",
      incomingVideo: "Incoming video call",
      connecting: "Connecting…",
      inCall: "In call",
      accept: "Accept",
      decline: "Decline",
      cancel: "Cancel call",
      mute: "Mute",
      unmute: "Unmute",
      cameraOn: "Turn camera on",
      cameraOff: "Turn camera off",
      shareScreen: "Share screen",
      stopSharing: "Stop sharing",
      fullscreen: "Full screen",
      exitFullscreen: "Exit full screen",

      end: "End call",
      retry: "Try again",
      ended: "Call ended",
      you: "You",
      noRecording: "Calls are never recorded.",
      waitingForOther: "Waiting for the other participant to join…",
      history: "Call history",
      historyEmpty: "No calls yet.",
      event: {
        incoming: "Incoming {{mode}}",
        outgoing: "{{mode}}",
        missed: "Missed {{mode}}",
        noAnswer: "{{mode}} — no answer",
        declined: "{{mode}} declined",
        youDeclined: "You declined the {{mode}}",
        canceled: "{{mode}} canceled",
        failed: "{{mode}} failed",
        ended: "{{mode}} ended",
      },
      status: {
        ended: "Completed",
        declined: "Declined",
        missed: "Missed",
        canceled: "Canceled",
        failed: "Failed",
        active: "Ongoing",
      },
      errors: {
        userBusy: "One of you is already on another call.",
        projectBusy: "There is already an active call in this project.",
        noClientAccount: "Your client hasn't created a Lumvio account yet, so calls aren't available.",
        expired: "That call is no longer available.",
        disabled: "Calls aren't switched on yet.",
        notConfigured: "Calling isn't available right now.",
        providerUnavailable: "The call service is unavailable. Please try again.",
        forbidden: "You're not a participant in this project.",
        generic: "The call could not be completed. Please try again.",
      },
    },
  },
} as const;
